import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm, ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDocumentTitle, useWizardModals, WMICWizardChangeOrRenewalPage } from 'wmic-pe-portals-wizard-components-ui';
import { DropdownMenuButton } from '@jutro/components';
import {
    CONSTANTS,
    FlowStepId,
    MODAL_CONSTANTS,
    parseErrorMessage,
    useCanJumpToWizardStepDispatch,
    WMICDriverUtil,
    WMICLogger,
    WMICUserAccessUtil,
    WMICValidationUtil,
    WMICVariousUtil,
} from 'wmic-pe-portals-utils-js';
import { WMICDriverDetailView } from 'wmic-pe-capability-gateway-common-pa-react';
import { messages as commonMessagesGW } from '@xengage/gw-platform-translations';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-policyrenewal-common-react';

import { WMICErrorHandler } from 'wmic-pe-capability-quoteandbind-common-react';

import { DropdownMenuLink } from '@jutro/router';
import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';
import messages from './WMICPAPolicyRenewalDriversDetailsPage.messages';
import metadata from './WMICPAPolicyRenewalDriversDetailsPage.metadata.json5';

const ANIS_PATH = 'baseData.additionalNamedInsureds_WMIC';
const { INSURED_ROLES } = CONSTANTS;

function WMICPAPolicyRenewalDriversDetailsPage(props) {
    const { wizardData: policyRenewalVM, updateWizardData } = props;
    const { setWizardLoading, showConfirm, showError } = useWizardModals();
    const { RenewalService } = useDependencies('RenewalService');
    const { authHeader, authUserData } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const [skeletonStruct, setSkeletonStruct] = useState({});
    const [additionalNamedInsureds, setAdditionalNamedInsureds] = useState([]);
    const [showErrors, setShowErrors] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const { initialValidation, isComponentValid, onValidate, registerInitialComponentValidation } = useValidation(
        'WMICPolicyRenewalPADriversDetailsPage'
    );
    const driversWithDuplicateLicence = WMICDriverUtil.getDriversWithDuplicateLicense(
        _.get(policyRenewalVM.value, RISK_PATHS.DRIVERS_PATH)
    );
    const { initDispatch, wizardStepDispatch } = useCanJumpToWizardStepDispatch();

    useDocumentTitle(translator(messages.driversTitle), policyRenewalVM);

    const initDriverData = useCallback(async () => {
        initDispatch(policyRenewalVM);
        const driversTmp = _.get(policyRenewalVM, RISK_PATHS.DRIVERS_PATH);
        const additionalNamedInsuredsSub = _.get(policyRenewalVM, `${ANIS_PATH}.value`);
        const additionalNamedInsuredsTmp = [];
        const skeletonStructure = await RenewalService.getSkeletonStructure(policyRenewalVM.value, authHeader);

        // pull out driver specific structure
        const driverStruct = _.get(skeletonStructure, RISK_PATHS.DRIVERS_PATH);

        setSkeletonStruct(driverStruct);

        if (additionalNamedInsuredsSub && !_.isEmpty(additionalNamedInsuredsSub)) {
            additionalNamedInsuredsSub.forEach(nameInsured => {
                const additionalNameInsured = viewModelService.create(
                    nameInsured,
                    'pc',
                    'wmic.edge.ca.capabilities.policyjob.draft.dto.AdditionalNamedInsuredDTO_WMIC'
                );

                _.set(additionalNameInsured, 'isDriver', false);

                const aniSubType = _.get(additionalNameInsured, 'subtype.value.code');

                if (aniSubType === CONSTANTS.Person.toLowerCase()) {
                    const isDriver = driversTmp.value.some(
                        driver =>
                            _.get(additionalNameInsured, 'contactPublicID.value') ===
                            _.get(driver, 'person.publicID.value')
                    );

                    _.set(additionalNameInsured, 'isDriver', isDriver);
                }

                additionalNamedInsuredsTmp.push(additionalNameInsured);
            });
            if (!_.isEmpty(additionalNamedInsuredsTmp)) {
                setAdditionalNamedInsureds(additionalNamedInsuredsTmp);
            }
        }

        const primaryNamedInsuredTmp = _.get(policyRenewalVM, 'baseData.primaryNamedInsured_WMIC.value');

        const isDriver = driversTmp.value.some(
            driver => _.get(primaryNamedInsuredTmp, 'contactPublicID') === _.get(driver, 'person.publicID')
        );

        _.set(primaryNamedInsuredTmp, 'isDriver', isDriver);
    }, [RenewalService, authHeader, policyRenewalVM, viewModelService]);

    useEffect(() => {
        registerInitialComponentValidation(() => _.get(policyRenewalVM, RISK_PATHS.DRIVERS_PATH).value.length > 0);

        initDriverData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canEditRenewal = useMemo(
        () => WMICUserAccessUtil.canEditRenewal(authUserData.permission_Ext),
        [authUserData.permission_Ext]
    );

    const onCreateDriver = useCallback(
        (index, namedInsured, insuredRelation) => {
            const driver = WMICDriverUtil.createDriver(index, skeletonStruct, namedInsured, insuredRelation);
            const driversVM = _.get(policyRenewalVM, RISK_PATHS.DRIVERS_PATH);
            const { _xCenter, _dtoName } = driversVM;
            const driverVM = viewModelService.create(driver, _xCenter, _dtoName, policyRenewalVM.aspects.context());
            const commercialDriverVM = viewModelService.create(
                {},
                'pc',
                'wmic.edge.ca.capabilities.policyjob.lob.personalauto.coverables.dto.CommercialDriverDTO_WMIC'
            ).value;

            _.set(driverVM, 'commercialDriver_WMIC', commercialDriverVM);

            if (policyRenewalVM.baseData?.isDriveChangeAvailable_Ext?.value) {
                const driveChangeVM = viewModelService.create(
                    {},
                    'pc',
                    'wmic.edge.ca.capabilities.policyjob.lob.personalauto.coverables.dto.DriveChangeDTO_WMIC'
                );
                driverVM.driveChange_WMIC = driveChangeVM.value;
            }

            if (namedInsured) {
                WMICDriverUtil.setAssignment(driverVM);

                if (
                    policyRenewalVM.baseData.primaryNamedInsured_WMIC.subtype.value === CONSTANTS.Person &&
                    policyRenewalVM.baseData.primaryNamedInsured_WMIC.publicID.value === driverVM.person.publicID.value
                ) {
                    driverVM.creditConsentReceived = policyRenewalVM.baseData.creditConsentReceived.value;
                    driverVM.creditConsentDate = policyRenewalVM.baseData.creditConsentDate.value;
                    driverVM.personalInfoConsentForm = !_.isUndefined(
                        policyRenewalVM.baseData.personalInfoConsentForm.value
                    )
                        ? policyRenewalVM.baseData.personalInfoConsentForm.value
                        : null;
                    driverVM.creditInfoWithdrawalConsent = !_.isUndefined(
                        policyRenewalVM.baseData.creditInfoWithdrawalConsent.value
                    )
                        ? policyRenewalVM.baseData.creditInfoWithdrawalConsent.value
                        : null;

                    if (
                        policyRenewalVM?.baseData?.isDriveChangeAvailable_Ext?.value &&
                        policyRenewalVM?.baseData?.primaryNamedInsured_WMIC?.driveChangeData_Ext?.value
                    ) {
                        const PNIdriveChangeData =
                            policyRenewalVM.baseData.primaryNamedInsured_WMIC.driveChangeData_Ext;

                        if (policyRenewalVM.baseData.isDriveChangeFloowIntEnabled_Ext.value) {
                            driverVM.driveChange_WMIC.isDriverAlreadyEnrolledInDriveChange.value =
                                PNIdriveChangeData.isAlreadyEnrolledWhenFloowIsActive.value;
                            driverVM.driveChange_WMIC.isEmailEnrolled.value = PNIdriveChangeData.isEmailEnrolled.value;
                        } else {
                            driverVM.driveChange_WMIC.isEnrolled.value =
                                PNIdriveChangeData.isEnrolledInOtherPolicies.value;
                            driverVM.driveChange_WMIC.previousDiscountPercentage.value =
                                PNIdriveChangeData.priorDiscount.value;
                        }
                    }
                }

                const ANI = _.find(
                    policyRenewalVM.baseData.additionalNamedInsureds_WMIC.value,
                    namedInsuredParam =>
                        namedInsuredParam.contactPublicID === driverVM.person.publicID.value ||
                        namedInsuredParam.publicID === driverVM.person.publicID.value
                );

                if (ANI) {
                    driverVM.creditConsentReceived = ANI.creditConsentReceived;
                    driverVM.creditConsentDate = ANI.creditConsentDate;
                    driverVM.personalInfoConsentForm = !_.isUndefined(ANI.personalInfoConsentForm)
                        ? ANI.personalInfoConsentForm
                        : null;
                    driverVM.creditInfoWithdrawalConsent = !_.isUndefined(ANI.creditInfoWithdrawalConsent)
                        ? ANI.creditInfoWithdrawalConsent
                        : null;
                    driverVM.person.publicID = ANI.contactPublicID;
                }
            }

            policyRenewalVM.lobData.personalAuto.coverables.drivers.value.push(driverVM.value);
            wizardStepDispatch({ type: 'riskAdded' });
            return driverVM;
        },
        [skeletonStruct, policyRenewalVM, viewModelService, wizardStepDispatch]
    );

    const onCreateDriverWithModal = useCallback(
        (index, namedInsured, insuredRelation) =>
            showConfirm({
                title: commonMessages.revalidationRequiredTitle,
                message: commonMessages.revalidationRequiredBody,
                status: MODAL_CONSTANTS.STATUS.WARNING,
                icon: MODAL_CONSTANTS.ICON.ERROR,
                confirmButtonText: commonMessagesGW.yes,
                cancelButtonText: commonMessagesGW.cancelModel,
            }).then(results => {
                if (results === CONSTANTS.MODAL_RESULT.CANCEL || results === CONSTANTS.MODAL_RESULT.CLOSE) {
                    return false;
                }
                return onCreateDriver(index, namedInsured, insuredRelation);
            }),
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onCreateDriver, policyRenewalVM, showConfirm]
    );

    const saveDriver = useCallback(
        async selectedDriver => {
            _.unset(policyRenewalVM.value, 'bindData');

            try {
                setWizardLoading(true, translator(messages.savingDriver));

                const driverId = selectedDriver.publicID ?? selectedDriver.tempID;

                policyRenewalVM.value = await RenewalService.saveDriver(driverId, policyRenewalVM.value, authHeader);

                updateWizardData(policyRenewalVM);

                setShowErrors(false);

                if (WMICValidationUtil.hasDtoValidationErrors(policyRenewalVM, FlowStepId.PA_DRIVERS)) {
                    WMICVariousUtil.scrollToTop();
                }
                wizardStepDispatch({ type: 'reset', vm: policyRenewalVM });
                return true;
            } catch (error) {
                WMICLogger.error('Save driver failed', error);
                WMICErrorHandler.processAsModal(parseErrorMessage(error)).catch(() => {
                    // do nothing
                });
                return false;
            } finally {
                setWizardLoading(false);
            }
            // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        },
        [setWizardLoading, translator, RenewalService, authHeader, policyRenewalVM, wizardStepDispatch]
    );

    const onSaveDriver = useCallback(selectedDriver => saveDriver(selectedDriver.value), [saveDriver]);

    const onSaveDriverMiddleware = useCallback(
        selectedDriver => {
            if (driversWithDuplicateLicence) {
                const otherDriverWithSameLicense =
                    driversWithDuplicateLicence.find(d => d.publicID !== selectedDriver?.value?.publicID) ||
                    driversWithDuplicateLicence[0];

                showError({
                    title: translator(messages.licenseNotUnique),
                    message: translator(messages.licenceMatchMsg, { driver: otherDriverWithSameLicense.displayName }),
                });
            }
        },
        [driversWithDuplicateLicence, showError, translator]
    );

    const deleteDriver = useCallback(
        async driverId => {
            _.unset(policyRenewalVM.value, 'bindData');

            try {
                setWizardLoading(true, translator(messages.removingDriver));

                const removeDriverResult = await RenewalService.removeDriver(
                    policyRenewalVM.jobID.value,
                    driverId,
                    authHeader
                );

                if (!_.isEmpty(removeDriverResult.errorMessages)) {
                    showError({
                        title: translator(messages.removingDriverErrorModalTitle),
                        message: removeDriverResult.errorMessages.join('\n'),
                    });
                    return false;
                }

                policyRenewalVM.value = removeDriverResult.renewalData;

                updateWizardData(policyRenewalVM);
                setShowErrors(false);

                return true;
            } catch (error) {
                WMICLogger.error('Save driver failed', error);
                WMICErrorHandler.processAsModal(
                    error,
                    policyRenewalVM.value.jobID,
                    policyRenewalVM.value.baseData.jobType
                );

                return false;
            } finally {
                setWizardLoading(false);
            }
            // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        },
        [setWizardLoading, translator, RenewalService, authHeader, showError, policyRenewalVM]
    );

    const onDeleteDriver = useCallback(
        driver => {
            const modalMsg = translator(messages.removeDriver, { driver: driver.displayName?.value });

            showConfirm({
                title: messages.removeDriverTitle,
                message: modalMsg,
                confirmButtonText: commonMessagesGW.yes,
                cancelButtonText: commonMessagesGW.noModel,
            })
                .then(async response => {
                    if (response === CONSTANTS.MODAL_RESULT.CONFIRM) {
                        return deleteDriver(driver.publicID?.value);
                    }
                })
                .catch(_.noop);
        },
        [translator, showConfirm, deleteDriver]
    );

    const onCancelDriver = () => {
        _.set(policyRenewalVM, 'isEditingPage.value', false);
        updateWizardData(policyRenewalVM);
        wizardStepDispatch({ type: 'reset', vm: policyRenewalVM });
    };

    const onNext = useCallback(async () => {
        if (!isComponentValid || !WMICDriverUtil.presentDriverList(policyRenewalVM)) {
            setShowErrors(true);

            return false;
        }

        if (canEditRenewal) {
            try {
                setWizardLoading(true, translator(commonMessages.savingTransactionDetails));

                policyRenewalVM.value = await RenewalService.saveRenewal([policyRenewalVM.value], authHeader);

                updateWizardData(policyRenewalVM);

                return policyRenewalVM;
            } finally {
                setWizardLoading(false);
            }
        }

        return false;
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComponentValid, canEditRenewal, setWizardLoading, translator, policyRenewalVM, RenewalService, authHeader]);

    const renderAddDriverButton = useCallback(
        ({ isEditing, onClick }) => {
            const drivers = _.get(policyRenewalVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []);
            const pni = _.get(policyRenewalVM, 'baseData.primaryNamedInsured_WMIC.value');

            return (
                <DropdownMenuButton
                    id="addNewDriverButton"
                    buttonText={translator({
                        id: 'quoteandbind.pa.views.pa-drivers-details.addNewDriver',
                        defaultMessage: 'meta.Add New Driver',
                    })}
                    icon="mi-add"
                    disabled={isEditing}
                >
                    {WMICDriverUtil.canContactBeAddedToDrivers(drivers, pni) && (
                        <DropdownMenuLink onClick={() => onClick(drivers.length, pni, INSURED_ROLES.PRIMARY_INSURED)}>
                            {pni.displayName}
                        </DropdownMenuLink>
                    )}
                    {additionalNamedInsureds.map(
                        (ani, index) =>
                            WMICDriverUtil.canContactBeAddedToDrivers(drivers, ani.value) && (
                                <DropdownMenuLink
                                    onClick={() =>
                                        onClick(
                                            drivers.length,
                                            _.get(policyRenewalVM, `${ANIS_PATH}.value[${index}]`),
                                            INSURED_ROLES.ADDITIONAL_INSURED
                                        )
                                    }
                                >
                                    {ani.displayName.value}
                                </DropdownMenuLink>
                            )
                    )}
                    <DropdownMenuLink
                        onClick={() => onClick(_.get(policyRenewalVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []).length)}
                    >
                        {translator(messages.addNewDriver)}
                    </DropdownMenuLink>
                </DropdownMenuButton>
            );
        },
        [additionalNamedInsureds, policyRenewalVM, translator]
    );

    const overrideProps = {
        atLeastOneMsgContainer: {
            visible: _.isEmpty(_.get(policyRenewalVM, `${RISK_PATHS.DRIVERS_PATH}.value`, [])),
            message: messages.atLeastOne,
        },
        requiredText: {
            visible: _.get(policyRenewalVM, RISK_PATHS.DRIVERS_PATH).length > 0,
        },
        driverListView: {
            value: _.get(policyRenewalVM, `${RISK_PATHS.DRIVERS_PATH}.children`, []),
            clickable: true,
            startOpen: true,
            readOnly: !canEditRenewal,
            VMData: [
                {
                    headerText: translator(messages.driverName),
                    path: 'displayName',
                },
                {
                    headerText: translator(messages.licenseNumber),
                    path: 'licenseNumber',
                },
                {
                    headerText: translator(messages.licenseProvince),
                    path: 'licenseState',
                    getData: (row, path) => _.get(row.value, path) || translator(messages.licenseProvinceOther),
                },
            ],
            toCreate: onCreateDriverWithModal,
            onSave: onSaveDriver,
            onSaveMiddleware: onSaveDriverMiddleware,
            onDelete: onDeleteDriver,
            toUndoCreate: () => {
                const drivers = _.get(policyRenewalVM.value, RISK_PATHS.DRIVERS_PATH);
                drivers.splice(drivers.length - 1, 1);
                _.set(policyRenewalVM.value, RISK_PATHS.DRIVERS_PATH, drivers);
                updateWizardData(policyRenewalVM);
            },
            detailViewComponent: WMICDriverDetailView,
            onValidate,
            renderAddButton: renderAddDriverButton,
            detailViewComponentProps: {
                jobVM: policyRenewalVM,
            },
            onCancel: onCancelDriver,
            showCancelModal: true,
        },
    };

    return (
        <WMICWizardChangeOrRenewalPage
            onNext={onNext}
            cancelLabel={translator(commonMessages.saveAndExit)}
            isSkipping={initialValidation}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={policyRenewalVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                showErrors={showErrors}
                onValidationChange={onValidate}
            />
        </WMICWizardChangeOrRenewalPage>
    );
}

WMICPAPolicyRenewalDriversDetailsPage.propTypes = wizardProps;

export default withViewModelService(WMICPAPolicyRenewalDriversDetailsPage);
